import { twMerge } from '@shared/themes';
import type { ComponentProps } from 'react';
import { LotteryNumber } from '../LotteryNumber/LotteryNumber';
import { LotteryNumbersDivider } from '../LotteryNumbersDivider/LotteryNumbersDivider';
import type { LotteryName } from '../lottery-purchases/types';
import type { LotteryDrawResult, LotteryNumberString } from '../lottery-results/types';

type MatchingInfo = {
  numbers?: LotteryNumberString[];
  extraNumbers?: LotteryNumberString[];
  isHighlighted?: boolean;
};

type LotteryNumbersProps = ComponentProps<'div'> & {
  line: LotteryDrawResult;
  lotteryName: LotteryName;
  matching?: MatchingInfo;
};

/**
 * A component that renders a row of lottery numbers depending on array of numbers passed.
 *
 * @component
 * @param {number[]} numbers - Array of numbers to render
 * @param {number[]} matchingNumbers - Array of numbers to match against (undefined if matching is not provided)
 * @param {boolean} isHighlighted - Whether matched numbers should be highlighted (green) instead of standard (black)
 * @returns {JSX.Element} List of LotteryNumber components
 */
function Numbers({ numbers, matchingNumbers, isHighlighted }: { numbers: LotteryNumberString[]; matchingNumbers?: LotteryNumberString[]; isHighlighted?: boolean }) {
  return numbers.map((number, index) => (
    <LotteryNumber className="paragraph-s-strong xs:paragraph-l-strong" key={index} isMatched={matchingNumbers?.includes(number) ?? undefined} isHighlighted={isHighlighted}>
      {number}
    </LotteryNumber>
  ));
}

/**
 * A component that renders lottery numbers in a row with optional extra numbers separated by a divider.
 * It's expected that css varialbe --lottery-color will control color of the divider and will be set on the parent level.
 *
 * @param {string} [className] - Additional CSS classes to apply to the container
 * @param {LotteryLine} line - The lottery line data containing numbers and optional extra numbers
 * @param {MatchingInfo} [matching] - Optional matching information for highlighting matched numbers
 * @returns {JSX.Element} A div containing the lottery numbers with optional divider and extra numbers
 */
function LotteryNumbers({ className, line, lotteryName, matching, ...props }: LotteryNumbersProps) {
  const { numbers, extraNumbers } = line;
  return (
    <div className={twMerge('flex items-center gap-1 xs:gap-2', className)} {...props}>
      <Numbers numbers={numbers} matchingNumbers={matching?.numbers} isHighlighted={matching?.isHighlighted} />
      <LotteryNumbersDivider isVisible={!!extraNumbers?.length} lotteryName={lotteryName} />
      <Numbers numbers={extraNumbers ?? []} matchingNumbers={matching?.extraNumbers} isHighlighted={matching?.isHighlighted} />
    </div>
  );
}

export { LotteryNumbers };
